import { forwardRef, useEffect, useMemo } from "react";
import { POST_SPIROMETRY_ENDPOINT } from "../../api/endpoints";
import {
    getPostSpirometryCalculatedValues,
    getPreSpirometryData,
    submitPostSprimetryData,
} from "../../api/forms/spirometry";
import {
    PostSpirometryData,
    PreSpirometryData,
    SpirometryFormType,
    SpirometryValidationErrors,
    UpdateSpirometryValidationErrors,
} from "../../api/forms/spirometry/types";
import { Nullable } from "../../api/types";
import { fetcher } from "../../api/utils";
import useDataInputForm, { FORM_IDS } from "../../hooks/useDataInputForm";
import useImperativeFormSubmit from "../../hooks/useImperativeFormSubmit";
import useTestId from "../../hooks/useTestId";
import { FormHandle, InputTypes } from "../../types";
import Error from "../Error";
import DataInputGenericForm, {
    DataInputGenericFormProps,
} from "./DataInputGenericForm";

type Props = {
    formValues: PostSpirometryData | null | undefined;
    setFormValues: React.Dispatch<
        React.SetStateAction<PostSpirometryData | null | undefined>
    >;
    setPreFormValues: React.Dispatch<
        React.SetStateAction<PreSpirometryData | null | undefined>
    >;
    setErrors: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    validationErrors: SpirometryValidationErrors["post"];
    updateValidationErrors: UpdateSpirometryValidationErrors;
};

export default forwardRef<
    FormHandle<Nullable<PostSpirometryData>> | null,
    Props
>(function PostSpirometyForm(props, ref) {
    const { formData, loading, error, setFromData } = useDataInputForm({
        formId: FORM_IDS.POST_SPIROMETRY,
    });

    const testId = useTestId();

    const {
        formValues,
        setFormValues,
        setPreFormValues,
        setErrors,
        validationErrors,
        updateValidationErrors,
    } = props;

    useEffect(() => {
        if (formData) {
            setFormValues(formData as PostSpirometryData);
        }
    }, [formData]);

    const { submissionLoading } = useImperativeFormSubmit<PostSpirometryData>({
        formValues,
        formData,
        ref,
        onSubmitSuccess: setFromData,
        submitFunction: submitPostSprimetryData,
    });

    type FormFields =
        DataInputGenericFormProps<PostSpirometryData>["formFields"];

    const formFields = useMemo<FormFields>(
        (): FormFields =>
            !formData || !formValues
                ? []
                : [
                      {
                          id: "fvc",
                          label: "FVC (l)",
                          value: formValues.fvc.measured ?? undefined,
                          originalValue: formData.fvc.measured ?? undefined,
                          secondaryValue: formValues.fvc.prePostPercent,
                          withSecondaryValue: true,
                          type: InputTypes.NUMBER,
                          required: false,
                          error: validationErrors?.fvc,
                      },
                      {
                          id: "fev1",
                          label: "FEV1 (l)",
                          value: formValues.fev1.measured ?? undefined,
                          originalValue: formData.fev1.measured ?? undefined,
                          secondaryValue: formValues.fev1.prePostPercent,
                          withSecondaryValue: true,
                          type: InputTypes.NUMBER,
                          required: false,
                          error: validationErrors?.fev1,
                      },
                      {
                          id: "fev1_fvc",
                          label: "FEV1/FVC (%)",
                          value: formValues.fev1_fvc.measured ?? undefined,
                          originalValue:
                              formData.fev1_fvc.measured ?? undefined,
                          secondaryValue: formValues.fev1_fvc.prePostPercent,
                          withSecondaryValue: true,
                          type: InputTypes.NUMBER,
                          required: false,
                          validation: {
                              isInteger: true,
                          },
                          error: validationErrors?.fev1_fvc,
                      },
                      {
                          id: "fef_25_75",
                          label: "FEF 25-75 (l)",
                          value: formValues.fef_25_75.measured,
                          originalValue: formData.fef_25_75.measured,
                          secondaryValue: formValues.fef_25_75.prePostPercent,
                          withSecondaryValue: true,
                          type: InputTypes.NUMBER,
                          required: false,
                          error: validationErrors?.fef_25_75,
                      },
                  ],
        [formData, formValues, validationErrors]
    );
    if (error) {
        return <Error />;
    }

    const onFromValuesChange = async ({
        value,
        fieldSettings: { id: fieldId },
    }: {
        value: Nullable<string | number>;
        fieldSettings: FormFields[number];
    }) => {
        const valueToSave =
            value === null || isNaN(Number(value))
                ? null // if not a number, don't update the value
                : Number(value);

        const validationRes = await fetcher(
            `${POST_SPIROMETRY_ENDPOINT}/validation?test_id=${testId}`,
            {
                method: "POST",
                body: JSON.stringify({
                    [fieldId]: { measured: valueToSave },
                }),
                headers: {
                    "Content-Type": "application/json",
                },
            },
            true
        );

        const hasError = Boolean(!validationRes || validationRes?.[fieldId]?.error_message);

        updateValidationErrors("post", {
            [fieldId]: hasError ? validationRes?.[fieldId].error_message : undefined,
        });

        setErrors((prev) => ({
            ...prev,
            [`postSpirometry_${fieldId}`]: hasError,
        }));

        const newValues = await getPostSpirometryCalculatedValues({
            value: valueToSave,
            fieldId,
            testId,
        });

        const newPreValues = (await getPreSpirometryData({
            testId: testId ?? "",
        })) as Nullable<PreSpirometryData>;

        setFormValues((prevFormValues: Nullable<PostSpirometryData>) => {
            if (!prevFormValues || !formData) {
                return prevFormValues;
            }
            return {
                ...prevFormValues,
                ...newValues,
            };
        });

        const updateValidationErrorsByFormType = (
            formType: SpirometryFormType,
            newSpirometryValues:
                | Partial<PreSpirometryData>
                | Partial<PostSpirometryData>
        ) => {
            const updatedValidationErrors: typeof validationErrors = {};
            for (const key in newSpirometryValues) {
                const _key = key as keyof typeof newSpirometryValues;
                updatedValidationErrors[_key] =
                    newSpirometryValues[_key]?.error_message;
            }
            updateValidationErrors(formType, updatedValidationErrors);
        };
        updateValidationErrorsByFormType("post", newValues!);
        updateValidationErrorsByFormType("pre", newPreValues!);

        setPreFormValues((prevFormValues: Nullable<PreSpirometryData>) => {
            if (!prevFormValues) {
                return prevFormValues;
            }
            return {
                ...prevFormValues,
                ...newPreValues,
            };
        });
    };

    return (
        <DataInputGenericForm
            formTitle="Post CPET Spirometry"
            formFields={formFields}
            formData={formValues}
            onFromValuesChange={onFromValuesChange}
            loading={loading || submissionLoading}
            headers={["Parameter", "Meas.", "Δ Pre-Post (%)"]}
        />
    );
});
